import { useCallback, useState } from "react";
import { z } from "zod";

import { ApiError, fetchFastApi } from "../api/fastapi";

const ResolvedSchema = z.object({ path: z.string() });

// The 409 detail: the workspace to switch to before resolving again.
const MismatchDetailSchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
});

export type LaunchOutcome =
  | { kind: "resolved"; path: string }
  | { kind: "switchWorkspace"; workspaceId: string; workspaceName: string };

// zod-native so consumers that validate untrusted values (the failed page's
// route state) reuse this schema instead of re-deriving keys with a cast.
export const LaunchFailureSchema = z.enum([
  "expired",
  "forbidden",
  "denied",
  "unreachable",
  "networkError",
  "serverError",
  "unknown",
]);
export type LaunchFailure = z.infer<typeof LaunchFailureSchema>;

// Prefix the backend uses on the `detail` of a missing-permission 403, as
// opposed to a 403 for not being a member — see optro_launch_router.py.
const MISSING_PERMISSION_PREFIX = "Missing permission";

// The resolve endpoint's structured 403 detail for a permission refusal.
const MissingPermissionDetailSchema = z.object({
  code: z.literal("missing_permission"),
});

// Map an error to the page's copy key. Only a network drop and a 502 get
// reachability copy; 503 is Midship's own hiccup (serverError), and nothing
// else unexpected may point the user at Optro.
const toFailure = (error: unknown): LaunchFailure => {
  if (error instanceof ApiError) {
    if (error.isNetworkError) return "networkError";
    if (error.status === 404) return "expired";
    if (error.status === 403) {
      if (MissingPermissionDetailSchema.safeParse(error.detailData).success) {
        return "denied";
      }
      // Legacy bare-string form, kept for older backends mid-deploy.
      if (
        typeof error.detail === "string" &&
        error.detail.startsWith(MISSING_PERMISSION_PREFIX)
      ) {
        return "denied";
      }
      return "forbidden";
    }
    if (error.status === 502) return "unreachable";
    // Midship's own intent store hiccuped — retryable, and NOT the user's
    // connection, so it must not get the check-your-connection copy.
    if (error.status === 503) return "serverError";
    return "unknown";
  }
  return "unknown";
};

// Outcomes: 200 = navigate to the resolved path, 409 = switch to the named
// workspace (membership already verified server-side); anything else sets
// failure copy and returns null.
export const useOptroLaunchResolve = () => {
  const [failure, setFailure] = useState<LaunchFailure | null>(null);

  const resolve = useCallback(
    async (token: string): Promise<LaunchOutcome | null> => {
      try {
        const response = await fetchFastApi({
          method: "POST",
          path: `/controls/v2-optro/launch/${encodeURIComponent(token)}/resolve`,
          throwOnErrorEnvelope: true,
        });
        return { kind: "resolved", path: ResolvedSchema.parse(response).path };
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          const detail = MismatchDetailSchema.safeParse(error.detailData);
          if (detail.success) {
            return {
              kind: "switchWorkspace",
              workspaceId: detail.data.workspace_id,
              workspaceName: detail.data.workspace_name,
            };
          }
        }
        setFailure(toFailure(error));
        return null;
      }
    },
    [],
  );

  return { resolve, failure };
};
